// The waves on the page: gusts that leave the butterfly, drift down the page, and end once
// their bands have passed every corner of the viewport and the portrait (core/gust.ts).
// Each new wave gets an id that its ring carries, the X-ray flashes as the waves' bands
// cross the face, and the page hears about every live wave's ring.

import {
  RIPPLE_DEFAULTS,
  WAVE_ORIGIN,
  rippleAmplitude,
  rippleFrontInto,
  waveLife,
  type GustPoint,
  type Ripple,
  type World,
} from "@bfly/engine";
import { pageScrollX, pageScrollY } from "../page/page-scroll.js";
import type { PortraitHooks, WaveRing, WaveStart } from "../waves/waves.js";
import { easeXray, xrayStrength } from "./xray-flash.js";
import type { Box } from "@bfly/engine";

/** A point given as a fraction of the portrait's own box, returned in viewport pixels. The
    portrait is rotated about its center, so its client rect alone can't place it. */
function portraitPoint(portrait: HTMLElement, u: number, v: number): GustPoint {
  const rect = portrait.getBoundingClientRect();
  const transform = getComputedStyle(portrait).transform;
  const matrix = new DOMMatrix(transform === "none" ? undefined : transform);
  const local = matrix.transformPoint(
    new DOMPoint((u - 0.5) * portrait.offsetWidth, (v - 0.5) * portrait.offsetHeight),
  );
  return { x: rect.left + rect.width / 2 + local.x, y: rect.top + rect.height / 2 + local.y };
}

/** The viewport's corners, relative to `origin`. */
function viewportCorners(origin: GustPoint): GustPoint[] {
  return [
    { x: -origin.x, y: -origin.y },
    { x: window.innerWidth - origin.x, y: -origin.y },
    { x: -origin.x, y: window.innerHeight - origin.y },
    { x: window.innerWidth - origin.x, y: window.innerHeight - origin.y },
  ];
}

export class PortraitWaves {
  // The portrait's box relative to the origin. The butterfly is on the portrait, so a
  // scroll moves both and the box stays valid.
  private portraitBox: Box = { left: 0, top: 0, right: 0, bottom: 0 };
  // How far the face reaches from the butterfly, in page pixels. The X-ray shows until a
  // wave's face ring has passed it.
  private faceReach = 0;
  // The X-ray's eased level (it eases toward the waves' strength in engine time), the time of
  // its last frame, the strength it shows, and whether `data-xray-flash` is on, so a frame
  // with no change writes no attribute.
  private xrayEased = 0;
  private xrayAt: number | null = null;
  private xrayShown = 0;
  private xrayFlash = false;
  // In page pixels, so a scroll moves the background ring with the butterfly.
  private originOnPage: GustPoint = { x: 0, y: 0 };
  // Two waves can start in one frame, so the page hears about each one, oldest first.
  private readonly ids = new WeakMap<Ripple, number>();
  private nextId = 1;
  // The live waves' rings, oldest first. The ring objects stay in the pool and get updated
  // in place, so frames don't allocate new ones.
  private readonly rings: WaveRing[] = [];
  private readonly ringPool: WaveRing[] = [];
  private hadWave = false;

  constructor(
    private readonly portrait: HTMLElement,
    private readonly world: World,
    private readonly hooks: Pick<PortraitHooks, "onWaves">,
  ) {}

  /** Measures where waves start and what they have to pass before they end. Runs on start
      and on each layout change. `faceReach` is the face's reach from the butterfly, in page
      pixels. */
  measure(faceReach: number): void {
    this.faceReach = faceReach;
    const origin = this.placeOrigin();
    const rect = this.portrait.getBoundingClientRect();
    const box = {
      left: rect.left - origin.x,
      top: rect.top - origin.y,
      right: rect.right - origin.x,
      bottom: rect.bottom - origin.y,
    };
    this.portraitBox = box;
    // A wave ends once its band has passed every corner of the viewport, plus the portrait's
    // corners where the portrait sticks out past the viewport.
    this.world.ripples.exitPoints = [
      ...viewportCorners(origin),
      { x: box.left, y: box.top },
      { x: box.right, y: box.top },
      { x: box.left, y: box.bottom },
      { x: box.right, y: box.bottom },
    ];
  }

  /** On scroll, a live wave keeps the full radius it got when it started. Only the next wave
      gets a fresh measurement. */
  scrolled(): void {
    if (this.world.ripples.items.length) this.placeOrigin();
    else this.measure(this.faceReach);
  }

  /** Every frame: tells the page about new waves, then about every live wave's ring. */
  update(): void {
    const { items } = this.world.ripples;
    // The live wave count, on the element, for tests and the curious.
    const count = String(items.length);
    if (this.portrait.dataset.waves !== count) this.portrait.dataset.waves = count;
    for (const item of items) {
      if (this.ids.has(item)) continue;
      const id = this.nextId++;
      this.ids.set(item, id);
      const detail: WaveStart = { id, level: item.level, source: item.source };
      this.portrait.dispatchEvent(new CustomEvent("jrsm-wave", { detail, bubbles: true }));
    }
    const wave = items.at(-1);
    if (wave && !this.world.reducedMotion) this.follow(items);
    else if (this.hadWave) this.end();
    this.hadWave = wave !== undefined;
  }

  /** No waves left: the page puts its words back and the X-ray goes away. */
  end(): void {
    this.xrayEased = 0;
    this.xrayAt = null;
    this.showXray(0);
    this.hooks.onWaves?.([]);
  }

  // Where waves start, in page pixels. Scrolling moves it once the portrait locks.
  private placeOrigin(): GustPoint {
    const origin = portraitPoint(this.portrait, WAVE_ORIGIN.x, WAVE_ORIGIN.y);
    this.originOnPage = { x: origin.x + window.scrollX, y: origin.y + window.scrollY };
    return origin;
  }

  private follow(items: readonly Ripple[]): void {
    const now = this.world.time;
    const originX = this.originOnPage.x - pageScrollX();
    const originY = this.originOnPage.y - pageScrollY();
    // Every wave starts at the butterfly.
    this.rings.length = 0;
    for (let i = 0; i < items.length; i++) {
      const item = items[i]!;
      const ring = (this.ringPool[i] ??= {
        id: 0,
        amplitude: 0,
        originX: 0,
        originY: 0,
        life: 0,
        x: 0,
        y: 0,
        radius: 0,
        thickness: 0,
        growth: 0,
        draw: item.gust,
      });
      ring.id = this.ids.get(item) ?? 0;
      ring.draw = item.gust;
      rippleFrontInto(ring, item, now, RIPPLE_DEFAULTS.dur, originX, originY);
      ring.amplitude = rippleAmplitude(item.amp, ring.radius);
      ring.originX = originX;
      ring.originY = originY;
      ring.life = (now - item.t0) / waveLife(RIPPLE_DEFAULTS.dur, item.strength);
      this.rings.push(ring);
    }
    // The X-ray shows where the face's dots scatter, eased along with each wave's face ring
    // (its mean radius and thickness, the same ones the face uses). The strongest wins, so a
    // new wave doesn't cut an older one's X-ray short.
    let xray = 0;
    for (const ring of this.rings)
      xray = Math.max(xray, xrayStrength(ring.radius, ring.thickness, this.faceReach));
    this.xrayEased = easeXray(this.xrayEased, xray, now - (this.xrayAt ?? now));
    this.xrayAt = now;
    // A level too faint to see counts as off, so the X-ray goes away between waves.
    this.showXray(this.xrayEased < 0.005 ? 0 : this.xrayEased);
    this.hooks.onWaves?.(this.rings);
  }

  /** How strongly the X-ray shows, 0 to 1, as of the last frame. */
  get xrayLevel(): number {
    return this.xrayShown;
  }

  private showXray(strength: number): void {
    this.xrayShown = strength;
    const flash = strength > 0;
    if (flash === this.xrayFlash) return;
    this.xrayFlash = flash;
    this.portrait.toggleAttribute("data-xray-flash", flash);
  }
}
